import { useRef, type PointerEvent as ReactPointerEvent } from 'react';

export function isTouchLikePointer(pointerType: string): boolean {
  return pointerType === 'touch' || pointerType === 'pen';
}

/**
 * I-1074 (measured on the iPad simulator, F-0519's desktop sidebar): a touch on a work area's or a
 * namespace's row reveals its reorder handle — **after the tap's `click`, never during its
 * `pointerdown`**.
 *
 * Revealed on `pointerdown`, the handle went from `opacity: 0` to visible while the finger was still
 * down. iOS WebKit's content-change observer reads a tap that makes content appear as "the page
 * showed a hover menu", and cancels the synthetic click: the first tap on a row only showed its
 * grip, and only a second tap opened the work area. On `click` the tap has already reached the row's
 * own button (which opens the work area), so the reveal can no longer cancel it.
 *
 * Nothing is lost for a drag: the handle is a separate target, so a drag never started from the
 * same touch that revealed it.
 */
export function useTouchReveal(reveal: () => void): {
  onPointerDown: (event: ReactPointerEvent) => void;
  onClick: () => void;
} {
  const touched = useRef(false);
  return {
    onPointerDown: (event) => {
      touched.current = isTouchLikePointer(event.pointerType);
    },
    onClick: () => {
      if (touched.current) reveal();
      touched.current = false;
    },
  };
}
